import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  organizationId: string;
  role: 'owner' | 'admin' | 'member';
  trialStartDate: string;
  trialEndsAt: string;
  plan: 'trial' | 'pro' | 'enterprise';
  createdAt: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  trialDaysLeft: number;
  signUp: (data: SignUpData) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  resetPassword: (email: string) => Promise<void>;
}

interface SignUpData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Calculer les jours restants de l'essai
  const calculateTrialDaysLeft = (trialEndsAt: string): number => {
    const now = new Date();
    const endDate = new Date(trialEndsAt);
    const diffTime = endDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const trialDaysLeft = user?.trialEndsAt ? calculateTrialDaysLeft(user.trialEndsAt) : 0;

  // Charger l'utilisateur depuis localStorage au démarrage
  useEffect(() => {
    const loadUser = () => {
      try {
        const token = localStorage.getItem('auth_token');
        const userData = localStorage.getItem('user_data');
        
        if (token && userData) {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
        }
      } catch (error) {
        // Nettoyer les données corrompues
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
      } finally {
        setIsLoading(false);
      }
    };

    loadUser();
  }, []);

  // Inscription
  const signUp = async (data: SignUpData): Promise<void> => {
    setIsLoading(true);
    
    try {
      // Simuler un délai réseau réduit pour inscription plus rapide
      await new Promise(resolve => setTimeout(resolve, 600));

      // Vérifier si l'email existe déjà
      const existingUsers = JSON.parse(localStorage.getItem('all_users') || '[]');
      const emailExists = existingUsers.some((u: User) => u.email === data.email);
      
      if (emailExists) {
        throw new Error('Cet email est déjà utilisé');
      }

      // Créer le nouvel utilisateur
      const now = new Date();
      const trialEnds = new Date(now);
      trialEnds.setDate(trialEnds.getDate() + 14);

      const newUser: User = {
        id: `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        organizationId: `org_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        role: 'owner',
        trialStartDate: now.toISOString(),
        trialEndsAt: trialEnds.toISOString(),
        plan: 'trial',
        createdAt: now.toISOString(),
      };

      // Générer un token JWT simulé
      const token = `jwt_${btoa(JSON.stringify({ userId: newUser.id, email: newUser.email }))}`;

      // Sauvegarder dans localStorage
      localStorage.setItem('auth_token', token);
      localStorage.setItem('user_data', JSON.stringify(newUser));
      
      // Ajouter à la liste des utilisateurs
      existingUsers.push(newUser);
      localStorage.setItem('all_users', JSON.stringify(existingUsers));

      // Sauvegarder le mot de passe (hashé en production)
      const passwords = JSON.parse(localStorage.getItem('user_passwords') || '{}');
      passwords[data.email] = data.password;
      localStorage.setItem('user_passwords', JSON.stringify(passwords));

      setUser(newUser);

      // Créer un agent par défaut en mode Draft
      const defaultAgent = {
        id: `agent_${Date.now()}`,
        name: 'Mon Premier Agent IA',
        type: '',
        status: 'draft',
        organizationId: newUser.organizationId,
        createdAt: now.toISOString(),
      };
      localStorage.setItem('default_agent', JSON.stringify(defaultAgent));

      // Marquer que l'utilisateur doit passer par l'onboarding
      localStorage.setItem('needs_onboarding', 'true');

      // Activer automatiquement l'essai gratuit 14 jours
      localStorage.setItem('trial_activated', 'true');
      localStorage.setItem('trial_start_date', now.toISOString());
      localStorage.setItem('trial_end_date', trialEnds.toISOString());

    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Connexion
  const login = async (email: string, password: string): Promise<void> => {
    setIsLoading(true);
    
    try {
      // Simuler un délai réseau réduit pour connexion plus rapide
      await new Promise(resolve => setTimeout(resolve, 500));

      // Vérifier les identifiants
      const allUsers = JSON.parse(localStorage.getItem('all_users') || '[]');
      const passwords = JSON.parse(localStorage.getItem('user_passwords') || '{}');

      const foundUser = allUsers.find((u: User) => u.email === email);
      
      if (!foundUser || passwords[email] !== password) {
        throw new Error('Email ou mot de passe incorrect');
      }

      // Générer un token
      const token = `jwt_${btoa(JSON.stringify({ userId: foundUser.id, email: foundUser.email }))}`;

      // Sauvegarder la session
      localStorage.setItem('auth_token', token);
      localStorage.setItem('user_data', JSON.stringify(foundUser));

      setUser(foundUser);

      // Vérifier s'il y a une URL de redirection sauvegardée
      const redirectUrl = sessionStorage.getItem('redirect_after_login');
      if (redirectUrl) {
        sessionStorage.removeItem('redirect_after_login');
        setTimeout(() => navigate(redirectUrl), 100);
      }

    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Déconnexion
  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('needs_onboarding');
    sessionStorage.removeItem('redirect_after_login');
    setUser(null);
    navigate('/');
  };

  // Réinitialisation du mot de passe
  const resetPassword = async (email: string): Promise<void> => {
    setIsLoading(true);
    
    try {
      // Simuler un délai réseau
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Vérifier si l'email existe
      const allUsers = JSON.parse(localStorage.getItem('all_users') || '[]');
      const userExists = allUsers.some((u: User) => u.email === email);
      
      if (!userExists) {
        throw new Error('Aucun compte associé à cet email');
      }

      // En production: envoyer un email avec un lien de réinitialisation

    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    trialDaysLeft,
    signUp,
    login,
    logout,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook personnalisé pour utiliser le contexte
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth doit être utilisé à l\'intérieur d\'un AuthProvider');
  }
  return context;
};